"use client";

import Link from "next/link";
import type { SearchResponse } from "@/app/lib/types";
import { Badge, SCPCodeBadge } from "./badge";

interface Props {
  results: SearchResponse;
  onPageChange: (page: number) => void;
}

export function ResultsTable({ results, onPageChange }: Props) {
  const { total, page, page_size, records } = results;
  const totalPages = Math.ceil(total / page_size);
  const start = (page - 1) * page_size + 1;
  const end = Math.min(page * page_size, total);

  function fmtAge(age?: number) {
    if (age === undefined || age === null) return "-";
    return age >= 300 ? "90+" : String(age);
  }

  return (
    <div>
      <div className="mb-2 text-sm text-gray-600">
        Showing {start}-{end} of {total.toLocaleString()} records
      </div>
      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium">ID</th>
              <th className="text-left px-3 py-2 font-medium">Age</th>
              <th className="text-left px-3 py-2 font-medium">Sex</th>
              <th className="text-left px-3 py-2 font-medium">Diagnoses</th>
              <th className="text-left px-3 py-2 font-medium">SCP Codes</th>
            </tr>
          </thead>
          <tbody>
            {records.map((rec) => (
              <tr key={rec.ecg_id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2">
                  <Link href={`/browse/ecg/${rec.ecg_id}`} className="text-blue-600 hover:underline font-mono text-xs">
                    {rec.ecg_id}
                  </Link>
                </td>
                <td className="px-3 py-2">{fmtAge(rec.age)}</td>
                <td className="px-3 py-2">{rec.sex === 1 ? "M" : rec.sex === 0 ? "F" : "-"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {rec.diagnostic_superclass.map((d) => (
                      <Badge key={d} label={d} type={d} />
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1 lg:hidden">
                    {rec.scp_codes.slice(0, 4).map((s) => (
                      <SCPCodeBadge key={s.code} code={s.code} description={s.description} />
                    ))}
                    {rec.scp_codes.length > 4 && <span className="text-xs text-gray-500">+{rec.scp_codes.length - 4}</span>}
                  </div>
                  <div className="hidden lg:flex flex-wrap gap-1">
                    {rec.scp_codes.slice(0, 10).map((s) => (
                      <SCPCodeBadge key={s.code} code={s.code} description={s.description} />
                    ))}
                    {rec.scp_codes.length > 10 && <span className="text-xs text-gray-500">+{rec.scp_codes.length - 10}</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button
          className="px-3 py-1 border rounded text-sm disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Prev
        </button>
        <span className="text-sm">
          Page {page} of {totalPages}
        </span>
        <button
          className="px-3 py-1 border rounded text-sm disabled:opacity-50"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}